import React, { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router-dom'
import { AdminUser } from '../../../queries/types'
import {
  Avatar,
  Content,
  Form,
  Indicators,
  Layout,
  Navigation,
  Responsive,
  theme,
} from 'ui'
import { useAdminUser, useDemoteUser, usePromoteUser } from '../../../queries'
import { nameForUser, userUrlForUser } from './helpers'
import { usePaginatedRoles } from './usePaginatedRoles'
import { UserForm } from './UserForm'
import { AdminRoleNavigationDrawer } from './AdminRoleNavigationDrawer'
import { useAccessLevelFilters } from './AccessLevelFilters'
import { AccessLevel } from '../../../queries/types'

export const AdminRoleEditView: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { accessLevel, setAccessLevel } = useAccessLevelFilters()

  // Drawer appearance
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('compact')

  const { search } = usePaginatedRoles()
  const { loading, data, error: loadError } = useAdminUser({ userId: id ?? '' })
  const user = data?.response?.user

  const demoteUser = useDemoteUser()
  const promoteUser = usePromoteUser()

  const userUrl = `${userUrlForUser(user)}${search}`
  const handleSuccess = () => {
    navigate(userUrl)
    setTimeout(() => {
      setAccessLevel(user?.accessLevel ?? accessLevel)
    }, 100)
  }

  const handleRoleChange = async (newAccessLevel: AccessLevel) => {
    if (!id) return

    try {
      if (
        newAccessLevel === AccessLevel.User &&
        user?.accessLevel === AccessLevel.Admin
      ) {
        await demoteUser.mutateAsync(id)
      } else if (
        newAccessLevel === AccessLevel.Admin &&
        user?.accessLevel === AccessLevel.User
      ) {
        await promoteUser.mutateAsync(id)
      }
      // Refresh the user data and navigate back
      handleSuccess()
    } catch (error) {
      console.error('Error changing user role:', error)
    }
  }

  return (
    <div
      className="w-full flex-grow flex-col flex sm:flex-row flex"
      style={{ opacity: loading ? 0.5 : 1 }}
    >
      <Helmet title={`${user?.firstName ?? 'User'} - MCSS Pro Connect`} />
      <AdminRoleNavigationDrawer />
      <Layout.PaddedContent>
        <div className="flex-col flex sm:flex-row flex">
          <article className="w-full flex-col flex mr-0 sm:mr-4">
            <Content.Body>
              <Navigation.ReturnLink to={userUrl}>
                Return to User
              </Navigation.ReturnLink>
            </Content.Body>
            <Content.Heading>
              {loading ? (
                <div className="flex">
                  <div className="mr-auto">
                    <Indicators.Spinner />
                  </div>
                </div>
              ) : (
                `Editing: ${user?.emailAddress ?? 'n/a'}`
              )}
            </Content.Heading>

            {loadError ? <Form.ErrorComponent error={loadError.message} /> : ''}

            {!loading && user && (
              <div className="mb-6">
                <Content.SubHeading>Access Level</Content.SubHeading>
                <div className="flex flex-col space-y-2 mt-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="accessLevel"
                      value={AccessLevel.Admin}
                      checked={user.accessLevel === AccessLevel.Admin}
                      onChange={() => handleRoleChange(AccessLevel.Admin)}
                      disabled={demoteUser.isPending || promoteUser.isPending}
                      className="mr-2"
                    />
                    <span>Admin</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="accessLevel"
                      value={AccessLevel.User}
                      checked={user.accessLevel === AccessLevel.User}
                      onChange={() => handleRoleChange(AccessLevel.User)}
                      disabled={demoteUser.isPending || promoteUser.isPending}
                      className="mr-2"
                    />
                    <span>Standard User</span>
                  </label>
                </div>
                {(demoteUser.isPending || promoteUser.isPending) && (
                  <div className="mt-2">
                    <Indicators.Spinner size="sm" className="mr-2" />
                    Updating role...
                  </div>
                )}
                {demoteUser.error && (
                  <Form.ErrorComponent error={demoteUser.error.message} />
                )}
                {promoteUser.error && (
                  <Form.ErrorComponent error={promoteUser.error.message} />
                )}
              </div>
            )}

            <UserForm
              user={user as User}
              loading={loading}
              onSuccess={handleSuccess}
            />
          </article>
          <div className="flex-col flex mb-auto mt-2 md:mt-4 w-full sm:w-1/2 md:w-1/3">
            <div className="mx-auto mb-2">
              {loading ? (
                <Indicators.Spinner size="4x" />
              ) : (
                <Avatar.Simple
                  color={theme.colorActionable}
                  name={nameForUser(user)}
                  maxInitials={1}
                  size={128}
                />
              )}
            </div>
          </div>
        </div>
      </Layout.PaddedContent>
    </div>
  )
}
